// filepath: /home/jono/dev/Eclipse_Dash/frontend/src/components/LeadDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, IconButton, Chip, Divider, Button, CircularProgress, Alert, Collapse } from '@mui/material';
import { X, Mail, Phone, MapPin, Calendar, Smartphone, Monitor, RotateCcw, User, Clock, Eye, Globe, ExternalLink, ClipboardCheck, Navigation, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import SiteMap from './SiteMap';
import PageViewsTable from './PageViewsTable';
import LeadConversationView from './LeadConversationView';
import axios from 'axios';

const LeadDetailsModal = ({ open, lead, onClose }) => {
    const [trakData, setTrakData] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mapLoading, setMapLoading] = useState(false);
    const [geoLocation, setGeoLocation] = useState(null);
    const [showConversation, setShowConversation] = useState(false);
    const [jobDescriptionCollapsed, setJobDescriptionCollapsed] = useState(true); // Collapsed by default

    // Eclipse Tint office address for the map route
    const ECLIPSE_TINT_ADDRESS = {
        street: "Unit 1/214 Bay St",
        suburb: "Brighton",
        state: "VIC",
        postcode: "3186",
        displayName: "Unit 1/214 Bay St, Brighton VIC 3186"
    };

    if (!lead) return null;

    // Helper function to determine device type
    const getDeviceInfo = (deviceString) => {
        if (!deviceString) return { type: 'Unknown', icon: Monitor };

        const device = deviceString.toLowerCase();
        if (device.includes('iphone') || device.includes('ipad')) {
            return { type: 'Mobile Apple', icon: Smartphone };
        } else if (device.includes('android')) {
            return { type: 'Mobile Android', icon: Smartphone };
        } else if (device.includes('mobile')) {
            return { type: 'Mobile', icon: Smartphone };
        } else {
            return { type: 'Desktop PC', icon: Monitor };
        }
    };

    const deviceInfo = getDeviceInfo(lead.Device);
    const DeviceIcon = deviceInfo.icon;

    // Format full name from database fields
    const fullName = `${lead.Customer_name || ''} ${lead.Customer_surname || ''}`.trim() || 'Unknown Customer';

    // Format full address from database fields
    const fullAddress = [lead.cust_address, lead.suburb, lead.pcode].filter(Boolean).join(', ') || 'Address not provided';

    // Check if the lead has a SimproQuote number - handle both field naming conventions
    const simproQuoteValue = lead.SimproQuote || lead.simpro_quote;
    const hasSimproQuote = simproQuoteValue &&
        simproQuoteValue !== 'null' &&
        simproQuoteValue !== 'undefined' &&
        simproQuoteValue.toString().trim() !== '';

    // Helper function to safely render potentially complex values
    const safeRender = (value) => {
        if (value === null || value === undefined) return 'Not specified';
        if (typeof value === 'object') {
            try {
                // Pretty format JSON for better readability
                return JSON.stringify(value, null, 2);
            } catch (e) {
                return 'Complex data';
            }
        }
        return value;
    };

    // Debug log to see lead data and if hasSimproQuote is true
    console.log('Lead data:', lead);
    console.log('SimproQuote value:', simproQuoteValue);
    console.log('hasSimproQuote:', hasSimproQuote);

    // Effect to geocode address once client data is available
    useEffect(() => {
        if (clientData && clientData.suburb && clientData.state) {
            const geocodeAddress = async () => {
                setMapLoading(true);
                try {
                    const address = [
                        clientData.address_line_1,
                        clientData.suburb,
                        clientData.state,
                        clientData.postcode,
                        'Australia'
                    ].filter(Boolean).join(', ');

                    console.log('Geocoding address:', address);

                    const response = await axios.get('https://nominatim.openstreetmap.org/search', {
                        params: {
                            q: address,
                            format: 'json',
                            limit: 1
                        }
                    });

                    if (response.data && response.data.length > 0) {
                        const { lat, lon } = response.data[0];
                        console.log('Geocoded coordinates:', { lat, lon });
                        setGeoLocation({ lat: parseFloat(lat), lng: parseFloat(lon) });
                    } else {
                        console.warn('No geocoding results found');
                        setError('Unable to locate address on map');
                    }
                } catch (err) {
                    console.error('Failed to geocode address:', err);
                    setError('Error locating address: ' + (err.message || err));
                } finally {
                    setMapLoading(false);
                }
            };

            geocodeAddress();
        }
    }, [clientData]);

    // Function to fetch data from Trak API
    const fetchTrakDetails = async () => {
        if (!hasSimproQuote) return;

        setLoading(true);
        setError(null);
        setClientData(null);
        setGeoLocation(null);

        console.log('Fetching Trak details for SimproQuote:', simproQuoteValue);

        try {
            // First, fetch the job details from Trak API
            const jobResponse = await axios.get(`/api/trak/job-with-client/${simproQuoteValue}`);

            if (!jobResponse.data) {
                throw new Error('No data returned from Trak API');
            }

            console.log('Trak job data:', jobResponse.data);
            setTrakData(jobResponse.data.job);

            // Set client data if available
            if (jobResponse.data.client) {
                console.log('Client data:', jobResponse.data.client);
                setClientData(jobResponse.data.client);
            }

        } catch (err) {
            console.error('Failed to fetch Trak details:', err);

            let errorMessage = 'Error fetching job details: ';

            if (err.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.error('Response error data:', err.response.data);
                console.error('Response error status:', err.response.status);
                console.error('Response error headers:', err.response.headers);

                if (err.response.status === 401) {
                    errorMessage += 'Authentication failed. Please check API credentials.';
                } else {
                    errorMessage += (err.response.data?.message || err.response.data?.error || `Status: ${err.response.status}`);
                }
            } else if (err.request) {
                // The request was made but no response was received
                console.error('Request made but no response received:', err.request);
                errorMessage += 'No response from server. Please check your network connection.';
            } else {
                // Something happened in setting up the request that triggered an Error
                errorMessage += err.message || 'Unknown error';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Calculate dynamic modal width based on active views
    const calculateModalWidth = () => {
        if (trakData && showConversation) {
            // Customer info + Trak details + Conversation (3 columns) - use most of screen but leave some margin
            return '95vw';
        } else if (trakData || showConversation) {
            // Customer info + either Trak details OR Conversation (2 columns)
            return '80vw';
        } else {
            // Customer info only (1 column)
            return '70vw';
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: calculateModalWidth(),
        maxWidth: 'none', // Remove maxWidth constraint
        maxHeight: '98vh',
        bgcolor: '#1a1a1a',
        boxShadow: '0 20px 40px rgba(0,0,0,0.7)',
        borderRadius: 3,
        overflow: 'auto',
        p: 0,
        border: '1px solid #333',
        color: '#e0e0e0'
    };

    console.log('Modal rendering with hasSimproQuote:', hasSimproQuote);
    console.log('Lead SimproQuote field:', lead.SimproQuote);
    console.log('Lead simpro_quote field:', lead.simpro_quote);

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                {/* Modal Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid #333',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#2d2d2d',
                    color: '#e0e0e0',
                    borderRadius: '12px 12px 0 0'
                }}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                        Lead #{lead.id} Details
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#e0e0e0',
                            '&:hover': { bgcolor: '#404040', color: '#ffffff' }
                        }}
                    >
                        <X />
                    </IconButton>
                </Box>

                {/* Trak Integration Button */}
                <Box sx={{
                    p: 2,
                    px: 3,
                    borderBottom: '1px solid #333',
                    bgcolor: '#2a2a2a',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2
                }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {hasSimproQuote ? (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ExternalLink size={16} />}
                                onClick={fetchTrakDetails}
                                disabled={loading || !!trakData}
                                sx={{
                                    textTransform: 'none',
                                    bgcolor: '#1976d2',
                                    '&:hover': { bgcolor: '#1565c0' }
                                }}
                            >
                                {trakData ? 'Trak Data Loaded' : 'Get Trak Details'}
                            </Button>
                        ) : (
                            <Typography variant="body2" sx={{ color: '#f44336' }}>
                                No Trak Job ID available for this lead
                            </Typography>
                        )}

                        {/* Messages Button */}
                        {(lead.mobileNumber || lead.phone) && (
                            <Button
                                variant={showConversation ? "contained" : "outlined"}
                                color="primary"
                                startIcon={<MessageSquare size={16} />}
                                onClick={() => setShowConversation(!showConversation)}
                                sx={{
                                    textTransform: 'none',
                                    bgcolor: showConversation ? '#1976d2' : 'transparent',
                                    borderColor: '#1976d2',
                                    color: showConversation ? '#fff' : '#1976d2',
                                    '&:hover': {
                                        bgcolor: showConversation ? '#1565c0' : 'rgba(25, 118, 210, 0.04)'
                                    }
                                }}
                            >
                                {showConversation ? 'Hide Messages' : 'Messages'}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Main Modal Content */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: (trakData || showConversation) ? 'row' : 'column',
                    bgcolor: '#1a1a1a'
                }}>
                    {/* Left Column: Lead Details */}
                    <Box sx={{
                        p: 3,
                        flex: trakData && showConversation ? '0 0 40%' : 1, // 40% when 3 columns, flex 1 otherwise
                        minWidth: trakData && showConversation ? '400px' : 'auto',
                        borderRight: (trakData || showConversation) ? '1px solid #333' : 'none'
                    }}>
                        {/* Customer Information */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{
                                color: '#ffffff',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <User size={20} />
                                Customer Information
                            </Typography>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                gap: 3,
                                p: 2,
                                bgcolor: '#2a2a2a',
                                borderRadius: 2,
                                border: '1px solid #333'
                            }}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Full Name
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 'medium' }}>
                                        {fullName}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Email Address
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: '#4fc3f7'
                                    }}>
                                        <Mail size={16} />
                                        <a
                                            href={`mailto:${lead.email_address || lead.email}`}
                                            style={{
                                                textDecoration: 'none',
                                                color: '#4fc3f7'
                                            }}
                                        >
                                            {lead.email_address || lead.email || 'Not provided'}
                                        </a>
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Mobile Number
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: '#81c784'
                                    }}>
                                        <Phone size={16} />
                                        <a
                                            href={`tel:${lead.mobileNumber || lead.phone}`}
                                            style={{
                                                textDecoration: 'none',
                                                color: '#81c784'
                                            }}
                                        >
                                            {lead.mobileNumber || lead.phone || 'Not provided'}
                                        </a>
                                    </Typography>
                                </Box>

                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Full Address
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: '#ffb74d'
                                    }}>
                                        <MapPin size={16} />
                                        {fullAddress}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Device Type
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: '#ba68c8'
                                    }}>
                                        <DeviceIcon size={16} />
                                        {deviceInfo.type}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Client ID
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                        {lead.trak_client_id || 'Not assigned'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 3, borderColor: '#333' }} />

                        {/* Booking & Service Information */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{
                                color: '#ffffff',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <Calendar size={20} />
                                Booking & Service Information
                            </Typography>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: 3,
                                p: 2,
                                bgcolor: '#2a2a2a',
                                borderRadius: 2,
                                border: '1px solid #333'
                            }}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Service Type
                                    </Typography>
                                    <Chip
                                        label={lead.requestType || lead.service_type || 'Unknown'}
                                        sx={{
                                            bgcolor: '#3f51b5',
                                            color: '#ffffff',
                                            fontWeight: 'bold'
                                        }}
                                        size="small"
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Booking Date
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                        {lead.booking_date ? format(new Date(lead.booking_date), 'PPP') : 'Not scheduled'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Booking Slot
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                        {lead.bookingSlot || lead.booking_slot || 'Not specified'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Lead Created
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: '#ffffff'
                                    }}>
                                        <Clock size={16} />
                                        {lead.TimeStampStart ? format(new Date(lead.TimeStampStart), 'PPP p') : 'Unknown'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Return Customer
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: lead.Return_Count > 0 ? '#4caf50' : '#f44336'
                                    }}>
                                        <RotateCcw size={16} />
                                        {lead.Return_Count > 0 ? `Yes (${lead.Return_Count} times)` : 'No'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Current Status
                                    </Typography>
                                    <Chip
                                        label={lead.status || 'Unknown'}
                                        sx={{
                                            bgcolor: '#2e7d32',
                                            color: '#ffffff',
                                            fontWeight: 'bold'
                                        }}
                                        size="small"
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 3, borderColor: '#333' }} />

                        {/* Page Views & Analytics */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{
                                color: '#ffffff',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <Eye size={20} />
                                Page Views & Analytics
                            </Typography>
                            <Box sx={{
                                p: 2,
                                bgcolor: '#2a2a2a',
                                borderRadius: 2,
                                border: '1px solid #333'
                            }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mb: 3 }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                            Total Page Views
                                        </Typography>
                                        <Typography variant="h4" sx={{ color: '#4fc3f7', fontWeight: 'bold' }}>
                                            {lead.page_view_count || 0}
                                        </Typography>
                                    </Box>
                                    {lead.first_page_view && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                                First Visit
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                                {format(new Date(lead.first_page_view), 'PPP p')}
                                            </Typography>
                                        </Box>
                                    )}
                                    {lead.last_page_view && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                                Last Visit
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                                {format(new Date(lead.last_page_view), 'PPP p')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Recent Page Views */}
                                {lead.page_views && lead.page_views.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 2 }}>
                                            Recent Page Views
                                        </Typography>
                                        <Box sx={{
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            border: '1px solid #444',
                                            borderRadius: 1,
                                            bgcolor: '#1a1a1a'
                                        }}>
                                            <PageViewsTable pageViews={lead.page_views} />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Qualification Notes */}
                        {(lead.Summary || lead.qualification_notes) && (
                            <>
                                <Divider sx={{ my: 3, borderColor: '#333' }} />
                                <Box>
                                    <Typography variant="h6" gutterBottom sx={{
                                        color: '#ffffff',
                                        fontWeight: 'bold'
                                    }}>
                                        Qualification Notes
                                    </Typography>
                                    <Box sx={{
                                        p: 3,
                                        bgcolor: '#2a2a2a',
                                        borderRadius: 2,
                                        border: '1px solid #333'
                                    }}>
                                        <Typography variant="body1" sx={{
                                            color: '#e0e0e0',
                                            lineHeight: 1.6
                                        }}>
                                            {lead.Summary || lead.qualification_notes}
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Middle Column: Trak Details */}
                    {trakData && (
                        <Box sx={{
                            p: 3,
                            flex: showConversation ? '0 0 40%' : 1, // 40% when 3 columns, flex 1 when 2 columns
                            minWidth: showConversation ? '400px' : 'auto',
                            maxWidth: showConversation ? '40%' : '50%',
                            bgcolor: '#1a1a1a',
                            borderLeft: '1px solid #333',
                            borderRight: showConversation ? '1px solid #333' : 'none',
                            overflowY: 'auto'
                        }}>
                            <Typography variant="h5" gutterBottom sx={{
                                color: '#ffffff',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <ClipboardCheck size={20} />
                                Trak Job Details
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            )}

                            {/* Job Information */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" gutterBottom sx={{
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    Job Information
                                </Typography>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: 3,
                                    p: 2,
                                    bgcolor: '#2a2a2a',
                                    borderRadius: 2,
                                    border: '1px solid #333'
                                }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                            Job Number
                                        </Typography>
                                        <Typography variant="body1" sx={{
                                            color: '#4fc3f7',
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem'
                                        }}>
                                            {typeof trakData.job_number === 'object'
                                                ? JSON.stringify(trakData.job_number)
                                                : trakData.job_number || 'N/A'}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                            Client Name
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                            {safeRender(trakData.client_name)}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                            Job Status
                                        </Typography>
                                        <Chip
                                            label={safeRender(trakData.status)}
                                            sx={{
                                                bgcolor: '#2e7d32',
                                                color: '#ffffff',
                                                fontWeight: 'bold'
                                            }}
                                            size="small"
                                        />
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                            Created Date
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                            {(() => {
                                                if (!trakData.created_at) return 'Not specified';
                                                try {
                                                    return format(new Date(trakData.created_at), 'PPP');
                                                } catch (e) {
                                                    return trakData.created_at;
                                                }
                                            })()}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 3, borderColor: '#333' }} />

                            {/* Job Location & Route */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" gutterBottom sx={{
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <Navigation size={20} />
                                    Job Location & Route
                                </Typography>

                                <Box sx={{
                                    p: 2,
                                    mb: 2,
                                    bgcolor: '#2a2a2a',
                                    borderRadius: 2,
                                    border: '1px solid #333'
                                }}>
                                    <Typography variant="body2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                        Customer Address:
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#ffb74d', mb: 1 }}>
                                        {clientData ? (
                                            [
                                                clientData.address_line_1,
                                                clientData.address_line_2
                                            ].filter(Boolean).join(', ') || 'Address not provided'
                                        ) : (
                                            safeRender(trakData.site_address?.street_address) || 'Address not provided'
                                        )}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#ffb74d' }}>
                                        {clientData ? (
                                            [
                                                clientData.suburb,
                                                clientData.state,
                                                clientData.postcode
                                            ].filter(Boolean).join(', ') || 'No additional address details'
                                        ) : (
                                            [
                                                safeRender(trakData.site_address?.suburb),
                                                safeRender(trakData.site_address?.state),
                                                safeRender(trakData.site_address?.postcode)
                                            ].filter(Boolean).join(', ') || 'No additional address details'
                                        )}
                                    </Typography>
                                </Box>

                                {geoLocation ? (
                                    <Box sx={{
                                        height: 400,
                                        position: 'relative',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        border: '1px solid #333'
                                    }}>
                                        {mapLoading ? (
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'rgba(0,0,0,0.7)',
                                                zIndex: 1
                                            }}>
                                                <CircularProgress size={60} sx={{ color: '#4fc3f7' }} />
                                                <Typography sx={{ ml: 2, color: '#ffffff' }}>
                                                    Loading map...
                                                </Typography>
                                            </Box>
                                        ) : null}

                                        <SiteMap
                                            siteLat={geoLocation.lat}
                                            siteLng={geoLocation.lng}
                                            siteName={clientData?.name || 'Job Location'}
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{
                                        p: 3,
                                        bgcolor: '#2a2a2a',
                                        borderRadius: 2,
                                        border: '1px solid #333',
                                        textAlign: 'center'
                                    }}>
                                        <Typography variant="body1" sx={{ color: '#f44336' }}>
                                            Unable to display map - geocoding information not available
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Divider sx={{ my: 3, borderColor: '#333' }} />

                            {/* Client Information */}
                            {clientData && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" gutterBottom sx={{
                                        color: '#ffffff',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <User size={20} />
                                        Client Information
                                    </Typography>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: 3,
                                        p: 2,
                                        bgcolor: '#2a2a2a',
                                        borderRadius: 2,
                                        border: '1px solid #333'
                                    }}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                                Client Name
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 'medium' }}>
                                                {safeRender(clientData.name)}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                                Email
                                            </Typography>
                                            <Typography variant="body1" sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: '#4fc3f7'
                                            }}>
                                                <Mail size={16} />
                                                <a
                                                    href={`mailto:${clientData.email}`}
                                                    style={{
                                                        textDecoration: 'none',
                                                        color: '#4fc3f7'
                                                    }}
                                                >
                                                    {safeRender(clientData.email) || 'Not provided'}
                                                </a>
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                                Phone
                                            </Typography>
                                            <Typography variant="body1" sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: '#81c784'
                                            }}>
                                                <Phone size={16} />
                                                <a
                                                    href={`tel:${clientData.phone}`}
                                                    style={{
                                                        textDecoration: 'none',
                                                        color: '#81c784'
                                                    }}
                                                >
                                                    {safeRender(clientData.phone) || 'Not provided'}
                                                </a>
                                            </Typography>
                                        </Box>

                                        {clientData.abn && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                                    ABN
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                                    {safeRender(clientData.abn)}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Box sx={{ gridColumn: '1 / -1' }}>
                                            <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                                Full Address
                                            </Typography>
                                            <Typography variant="body1" sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: '#ffb74d'
                                            }}>
                                                <MapPin size={16} />
                                                {[
                                                    clientData.address_line_1,
                                                    clientData.address_line_2,
                                                    clientData.suburb,
                                                    clientData.state,
                                                    clientData.postcode,
                                                    clientData.country
                                                ].filter(Boolean).join(', ') || 'Address not provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {/* Client Custom Fields */}
                            {clientData && clientData.custom_fields && Object.keys(clientData.custom_fields).length > 0 && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" gutterBottom sx={{
                                        color: '#ffffff',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <User size={20} />
                                        Client Custom Fields
                                    </Typography>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: 3,
                                        p: 2,
                                        bgcolor: '#2a2a2a',
                                        borderRadius: 2,
                                        border: '1px solid #333'
                                    }}>
                                        {Object.entries(clientData.custom_fields).map(([key, value]) => (
                                            <Box key={key}>
                                                <Typography variant="subtitle2" sx={{ color: '#9e9e9e', mb: 0.5 }}>
                                                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                </Typography>
                                                {(() => {
                                                    if (value === null) return <Typography variant="body1" sx={{ color: '#ffffff' }}>Not specified</Typography>;

                                                    if (typeof value === 'object') {
                                                        try {
                                                            // Format JSON with indentation for better readability
                                                            return (
                                                                <Box sx={{
                                                                    maxHeight: '200px',
                                                                    overflow: 'auto',
                                                                    backgroundColor: '#1a1a1a',
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    whiteSpace: 'pre-wrap'
                                                                }}>
                                                                    <pre style={{ margin: 0, color: '#e0e0e0', fontSize: '0.875rem' }}>
                                                                        {JSON.stringify(value, null, 2)}
                                                                    </pre>
                                                                </Box>
                                                            );
                                                        } catch (e) {
                                                            return <Typography variant="body1" sx={{ color: '#ffffff' }}>Invalid object structure</Typography>;
                                                        }
                                                    }

                                                    // Check if the value appears to contain HTML
                                                    if (typeof value === 'string' && (value.includes('<') && value.includes('>'))) {
                                                        return <Box sx={{ color: '#ffffff' }} dangerouslySetInnerHTML={{ __html: value }} />;
                                                    }

                                                    return <Typography variant="body1" sx={{ color: '#ffffff' }}>{value || 'Not specified'}</Typography>;
                                                })()}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Job Description */}
                            <Box sx={{ mb: 4 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.8
                                        }
                                    }}
                                    onClick={() => setJobDescriptionCollapsed(!jobDescriptionCollapsed)}
                                >
                                    <Typography variant="h6" gutterBottom sx={{
                                        color: '#ffffff',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 0,
                                        flex: 1
                                    }}>
                                        Job Description
                                    </Typography>
                                    {jobDescriptionCollapsed ? (
                                        <ChevronDown size={20} color="#ffffff" />
                                    ) : (
                                        <ChevronUp size={20} color="#ffffff" />
                                    )}
                                </Box>
                                <Typography variant="caption" sx={{
                                    color: '#9e9e9e',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    mt: 0.5,
                                    mb: 1
                                }}>
                                    Click to {jobDescriptionCollapsed ? 'expand' : 'collapse'} Job Description
                                </Typography>
                                <Collapse in={!jobDescriptionCollapsed}>
                                    <Box sx={{
                                        p: 2,
                                        bgcolor: '#2a2a2a',
                                        borderRadius: 2,
                                        border: '1px solid #333',
                                        mt: 2
                                    }}>
                                        {(() => {
                                            const description = trakData.description;
                                            if (!description) return <Typography variant="body1" sx={{ color: '#e0e0e0' }}>No description available</Typography>;

                                            // Check if the content appears to contain HTML
                                            if (typeof description === 'string' && (description.includes('<') && description.includes('>'))) {
                                                return <Box sx={{ color: '#e0e0e0' }} dangerouslySetInnerHTML={{ __html: description }} />;
                                            } else {
                                                return <Typography variant="body1" sx={{ color: '#e0e0e0', whiteSpace: 'pre-line' }}>
                                                    {safeRender(description)}
                                                </Typography>;
                                            }
                                        })()}
                                    </Box>
                                </Collapse>
                            </Box>
                        </Box>
                    )}

                    {/* Right Column: Conversation View */}
                    {showConversation && (
                        <Box sx={{
                            flex: trakData ? '1' : '1', // Use flex: 1 to fill available space
                            bgcolor: '#1a1a1a',
                            borderLeft: '1px solid #333',
                            display: 'flex',
                            alignItems: 'flex-start', // Align to top of customer information
                            justifyContent: 'flex-start',
                            p: 2, // Add some padding around the conversation view
                            minWidth: 0 // Allow shrinking below minimum content size
                        }}>
                            <LeadConversationView lead={lead} />
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default LeadDetailsModal;
